import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { PortugueseWithholdingDto } from '@app/data/dtos/ptrab/withholding/portuguese-withholding.dto';
import { WithholdingDto } from '@app/data/dtos/ptrab/withholding/withholding.dto';
import {
  mapPortugueseWithholdingData,
  mapWithholding,
  mapWithholdingData,
  mapEditWithholdingDto
} from '@app/data/mappers/ptrab/withholding.mappers';
import { WithholdingRepository } from '@app/domain/repositories/ptrab/withholding/withholding.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { IDataResponse } from '@app/entities/interfaces/data/data.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorCodes, ErrorMessages } from '@app/entities/models/error/error.model';
import { Company } from '@app/entities/models/ptrab/shared/company.model';
import { PortugueseWithholding, Withholding } from '@app/entities/models/ptrab/withholding/withholding.model';
import { Logger } from '@app/services';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';

@Injectable()
export class WithholdingRepositoryImpl extends MRepository implements WithholdingRepository {
  private logger = new Logger('WithholdingService');
  private onError = new Subject<ErrorMsgEnum>();
  onError$ = this.onError.asObservable();

  withholdingUrl = '/ptrab/payslip/withholding';

  constructor() {
    super(`${ENV.API_URL_PTRAB}`);
  }

  getWithholdingList(): Observable<Withholding[]> {
    return this.get<IDataResponse<WithholdingDto[]>>(this.withholdingUrl, {
      context: new HttpContext().set(SECTION_KEY, 'irpf-withholding')
    }).pipe(map(mapWithholdingData), catchError(this.handleError));
  }

  editWithholding(withholding: Withholding, authorization: TwoFactorAuthorization): Observable<Withholding> {
    const params = mapEditWithholdingDto(withholding);
    const request = {
      body: {
        params,
        authorization
      }
    };

    return this.post<WithholdingDto>(this.withholdingUrl, request).pipe(map(mapWithholding));
  }

  editWithholdingOld(
    withholding: Withholding,
    twoFactorAuthorization: TwoFactorAuthorization
  ): Observable<Withholding> {
    const company: Company = withholding.company;

    const request = {
      body: {
        params: {
          company_code: company.code,
          employee_number: company.employee_number,
          voluntary: withholding.voluntary
        },
        authorization: twoFactorAuthorization
      }
    };

    return this.post<WithholdingDto>(this.withholdingUrl, request).pipe(map(mapWithholding));
  }

  getPortugueseWithholdingList(isEmployee: boolean): Observable<PortugueseWithholding> {
    const url = isEmployee ? `${this.withholdingUrl}/list` : `${this.withholdingUrl}/list/exemployee`;

    return this.get<IDataResponse<PortugueseWithholdingDto>>(url).pipe(
      map(mapPortugueseWithholdingData),
      catchError((error: HttpErrorResponse) => {
        const errorType =
          error && error.status === ErrorCodes.LOCKED ? ErrorMsgEnum.SERVICE_LOCKED : ErrorMsgEnum.SERVER_ERROR;
        this.onError.next(errorType);

        return of([] as MSafeAny);
      })
    );
  }

  private handleError = (error: HttpErrorResponse): Observable<MSafeAny[]> => {
    let errorType;

    switch (error.status) {
      case ErrorCodes.LOCKED:
        errorType = ErrorMsgEnum.SERVICE_LOCKED;
        break;
      case ErrorCodes.SECTION_MAINTENANCE:
        if (error.error?.code === ErrorMessages.PERM_DEACTIVATED) {
          errorType = ErrorMsgEnum.SECTION_MAINTENANCE;
        } else {
          errorType = ErrorMsgEnum.SERVER_ERROR;
        }

        break;
      default:
        errorType = ErrorMsgEnum.SERVER_ERROR;
        break;
    }

    this.onError.next(errorType);
    this.logger.error(error);

    return of([]);
  };
}
